import * as React from 'react';
import { ActionSuggestion } from '../Components/ActionSuggestion';
import { CalendarCreateEventButton } from '../Buttons/CalendarCreateEventButton';
import { AlertModal } from '../Modals/AlertModal';
import { ActivityModal } from '../Modals/ActivityModal';
import { UserActivity } from '../../MedicareProAPI/Entities/User/UserActivity';
import type { ActionType } from '../../MedicareProAPI/Entities/User/UserActionSuggestion';
import {
  ACTION_TYPES,
  STATUS_TYPES,
  StatusType,
} from '../../MedicareProAPI/Entities/User/UserActionSuggestion';
import { UserActionSuggestionRepository } from '../../MedicareProAPI/Repositories/User/UserActionSuggestionRepository';
import { IntegrationManager } from '../Components/IntegrationManager';
import { INTEGRATION_TYPE, INTEGRATION_OPTIONS } from '../Constants/Integrations';
import { UserIntegrationOAuthVoipResourceRepository } from '../../MedicareProAPI';

interface UserActionSuggestionsPanelProps {
  userAccessToken: string;
  userId: number;
  usersAgencyId?: number;
  directUplineId?: number;
  absoluteUplineId?: number;
  staffId?: number;
  onUpdate?: () => void;
}

/**
 * Panel for viewing and managing AI-driven user action suggestions.
 *
 * Responsible for:
 * - Fetching paginated suggestions from the API
 * - Rendering suggestion cards with action handlers
 * - Converting suggestions into activities, alerts, or appointments
 * - Handling VoIP integration availability and setup
 *
 * @component
 * @author Andrew Brown
 * @date Created: 2025-12-04
 * @lastUpdated 2025-12-08
 * @version 1.3.0
 */
export const UserActionSuggestionsPanel = ({
  userAccessToken,
  userId,
  usersAgencyId,
  directUplineId,
  absoluteUplineId,
  staffId,
  onUpdate,
}: UserActionSuggestionsPanelProps) => {
  const PAGE_SIZE = 25;

  /** Repository for loading and updating user action suggestions. */
  const userActionSuggestionRepository = React.useMemo(() => {
    const repo = new UserActionSuggestionRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  /** Repository for querying VoIP integration feature availability. */
  const userResourceRepository = React.useMemo(() => {
    const repo = new UserIntegrationOAuthVoipResourceRepository();
    repo.setAccessToken(userAccessToken);
    return repo;
  }, [userAccessToken]);

  const [suggestions, setSuggestions] = React.useState<any[]>([]);
  const [isMakingActivity, setIsMakingActivity] = React.useState<boolean>(false);
  const [isMakingAlert, setIsMakingAlert] = React.useState<boolean>(false);
  const [isMakingAppointment, setIsMakingAppointment] = React.useState<boolean>(false);
  const [tempActivity, setTempActivity] = React.useState<UserActivity>();
  const [suggestion, setSuggestion] = React.useState<any>();
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

  const [fetching, setFetching] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(0);
  const [hasMore, setHasMore] = React.useState<boolean>(true);

  const [showIntegrationSetup, setShowIntegrationSetup] = React.useState<boolean>(false);
  const [allowedIntegrationServices, setAllowedIntegrationServices] = React.useState<Array<string>>(
    []
  );

  const [hasAvailableService, setHasAvailableService] = React.useState<boolean>(false);

  /** Sentinel element used by the IntersectionObserver for infinite scroll. */
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  /**
   * Locate a suggestion by its identifier.
   *
   * @param id - Suggestion ID.
   * @returns The matching suggestion instance, if found.
   */
  const getSuggestion = (id: number) => suggestions.find((item) => item.getId() === id);

  /**
   * Handle a user-initiated action on a suggestion.
   *
   * Triggers the appropriate modal/workflow based on the selected action
   * (appointment, alert, activity, or dismissal).
   *
   * @param id - Suggestion identifier.
   * @param action - Selected action type.
   */
  const onActionClick = (id: number, action: ActionType): void => {
    const found = getSuggestion(id);
    if (!found) return;

    setSuggestion(found);

    switch (action) {
      case ACTION_TYPES.APPOINTMENT:
        setIsMakingAppointment(true);
        break;

      case ACTION_TYPES.ALERT:
        setIsMakingAlert(true);
        break;

      case ACTION_TYPES.ACTIVITY:
        setIsMakingActivity(true);
        const newActivity = new UserActivity();
        newActivity
          .setActivityDetails(found.getSuggestion())
          .setActivityTitle(found.getSuggestion())
          .setActivityAffiliation(found.getInd().getIndId());
        setTempActivity(newActivity);
        break;

      case ACTION_TYPES.DISMISS:
        const payload = {
          status: STATUS_TYPES.DISMISSED,
        };
        handleUpdate(id, payload, ACTION_TYPES.DISMISS);
        break;
    }
  };

  /**
   * Persist a suggestion update to the API and reflect it in local state.
   *
   * Typically used when a suggestion is converted or dismissed.
   *
   * @param id - Suggestion identifier.
   * @param payload - Partial update payload (e.g. status/targetType).
   * @param action - Action that triggered the update.
   */
  const handleUpdate = async (id: number, payload: any, action: ActionType): Promise<void> => {
    setIsUpdating(true);

    await userActionSuggestionRepository.updateUserActionSuggestion(userId, id, payload);

    // Apply updates to local suggestions copy and trigger render
    setSuggestions((prev) =>
      prev.map((suggestion) => {
        if (suggestion.getId() !== id) return suggestion;

        const updated = suggestion.clone
          ? suggestion.clone()
          : Object.assign(Object.create(Object.getPrototypeOf(suggestion)), suggestion);

        if (payload?.status) {
          updated.setStatus(payload.status);
        }

        if (payload?.targetType) {
          updated.setTargetType(payload.targetType);
        }

        return updated;
      })
    );

    setIsUpdating(false);
    setIsMakingAppointment(false);
    setIsMakingAlert(false);
    setIsMakingActivity(false);
    setSuggestion(null);
    onUpdate ? onUpdate() : '';
  };

  /**
   * Fetch a page of suggestions from the API.
   *
   * - `pageToLoad = 0` replaces existing items.
   * - `pageToLoad > 0` appends to the existing list.
   *
   * Also computes `hasMore` from the accumulated list length vs. API total.
   *
   * @param pageToLoad - Zero-based page index to load.
   */
  const fetchSuggestions = async (pageToLoad: number) => {
    if (fetching) return; // avoid concurrent fetches

    setFetching(true);
    try {
      const offset = pageToLoad * PAGE_SIZE;

      const response = await userActionSuggestionRepository.getUserActionSuggestions(userId, {
        criteria: { status: 'pending' },
        limit: PAGE_SIZE,
        offset,
      });

      const items = response.getItems() || [];
      const total = response.getTotal();

      setSuggestions((prev) => {
        const next = pageToLoad === 0 ? items : [...prev, ...items];

        const loadedSoFar = next.length;

        setHasMore(loadedSoFar < total);
        return next;
      });

      setPage(pageToLoad);
    } catch (e) {
      console.error('Failed to fetch suggestions', e);
    } finally {
      setFetching(false);
    }
  };

  /**
   * Open the integration manager pre-filtered to a specific service type.
   *
   * @param serviceType - Integration type to configure (e.g. summary).
   */
  const handleSetUpIntegration = (serviceType: INTEGRATION_TYPE) => {
    setShowIntegrationSetup(true);
    setAllowedIntegrationServices([...INTEGRATION_OPTIONS[serviceType]]);
  };

  /**
   * Close the integration manager and reset its allowed services.
   */
  const handleSetUpIntegrationHide = () => {
    setShowIntegrationSetup(false);
    setAllowedIntegrationServices([]);
  };

  /**
   * Fetch service feature integration availability for the current user.
   *
   * If a supported service is available, trigger the initial suggestions load.
   */
  const fetchAvailability = () => {
    userResourceRepository
      .getUserIntegrationFeatureAvailability(userId, 'summary')
      .then((response) => {
        const length = response.length > 0;
        //length = true //REMOVE BEFORE DEPLOY
        setHasAvailableService(length);
        length ? fetchSuggestions(0) : '';
      });
  };

  /**
   * Initial load/reset when the user changes.
   *
   * Resets pagination state, clears existing suggestions, and checks
   * VoIP integration availability before loading suggestions.
   */
  React.useEffect(() => {
    setSuggestions([]);
    setHasMore(true);
    setPage(0);
    fetchAvailability();
  }, [userId]);

  /**
   * IntersectionObserver setup for infinite scroll.
   *
   * Observes the sentinel element and loads the next page when it
   * becomes visible, provided there are more items to load.
   */
  React.useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first.isIntersecting) return;
        if (fetching) return;
        if (!hasMore) return;

        fetchSuggestions(page + 1);
      },
      {
        root: null,
        rootMargin: '0px 0px 200px 0px',
        threshold: 0,
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [page, hasMore, fetching, userId]);

  return (
    <>
      {/* Suggestions list */}
      {suggestions.length > 0 &&
        suggestions.map((item, itemIndex) => (
          <ActionSuggestion
            key={`suggestion-item-${item.getId?.() ?? itemIndex}`}
            userAccessToken={userAccessToken}
            userId={userId}
            suggestion={item}
            onActionClick={onActionClick}
            isUpdating={suggestion?.getId() === item.getId() && isUpdating}
          />
        ))}

      {/* Sentinel for infinite scroll */}
      {suggestions.length > 0 && <div ref={sentinelRef} style={{ height: 1, marginTop: 8 }} />}

      {/* Empty state: feature not available to this user */}
      {suggestions.length === 0 && !fetching && !hasAvailableService && (
        <div className="ai-suggestions mb-3">
          <p className="mt-2">
            Currently, AI Suggestions use your VoIP call summaries to surface helpful follow-ups
            here.
          </p>
          <p className="mt-2">
            When available, each suggestion can be turned into an activity, alert, or appointment
            with just a few clicks. We’ll also be adding support for more integrations and services
            over time.
          </p>
          <p>
            Current version supports call summaries generated by OpenPhone (Quo) and Dialpad. v2 is
            planned to support Zoom phone and RingCentral call summaries.
          </p>
        </div>
      )}

      {/* Empty state: feature available but no current suggestions */}
      {suggestions.length === 0 && !fetching && hasAvailableService && (
        <div className="ai-suggestions mb-3">
          <p>
            <b>No Suggestions</b>
          </p>
          <p className="mt-2">
            Currently, AI Suggestions use your VoIP call summaries to surface helpful follow-ups
            here.
          </p>
          <p className="mt-2">
            When available, each suggestion can be turned into an activity, alert, or appointment
            with just a few clicks. We’ll also be adding support for more integrations and services
            over time.
          </p>
          <p>
            Current version supports call summaries generated by OpenPhone (Quo) and Dialpad. v2 is
            planned to support Zoom phone and RingCentral call summaries.
          </p>
          <p className="mt-2">
            If you currently use one of the supported services but do not see suggestion items
            listed here, you may need to{' '}
            <span
              className="badge badge-pill badge-info"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSetUpIntegration(INTEGRATION_TYPE.SUMMARY)}
            >
              reconnect
            </span>{' '}
            your account to allow MPCRM to ingest this data.
          </p>
        </div>
      )}

      {/* Loader */}
      {fetching && (
        <div className="mt-2">
          <i className="far fa-spinner fa-spin"></i> loading...
        </div>
      )}

      {isMakingAppointment && suggestion && (
        <CalendarCreateEventButton
          indId={suggestion.getInd().getIndId()}
          directInstantiation={true}
          onEventCreate={() =>
            handleUpdate(
              suggestion.getId(),
              { status: STATUS_TYPES.CONVERTED, targetType: ACTION_TYPES.APPOINTMENT },
              ACTION_TYPES.APPOINTMENT
            )
          }
        />
      )}

      {isMakingAlert && suggestion && (
        <AlertModal
          userAccessToken={userAccessToken}
          indId={suggestion.getInd().getIndId()}
          userId={userId}
          alertBody={suggestion.getSuggestion()}
          onCreate={() =>
            handleUpdate(
              suggestion.getId(),
              { status: STATUS_TYPES.CONVERTED, targetType: ACTION_TYPES.ALERT },
              ACTION_TYPES.ALERT
            )
          }
          onClose={() => setIsMakingAlert(false)}
        />
      )}

      {isMakingActivity && suggestion && (
        <ActivityModal
          userAccessToken={userAccessToken}
          userId={userId}
          agencyId={usersAgencyId}
          directUplineId={directUplineId}
          absoluteUplineId={absoluteUplineId}
          staffId={staffId}
          indId={suggestion.getInd().getIndId()}
          activity={tempActivity}
          show={true}
          onCreate={() =>
            handleUpdate(
              suggestion.getId(),
              { status: STATUS_TYPES.CONVERTED, targetType: ACTION_TYPES.ACTIVITY },
              ACTION_TYPES.ACTIVITY
            )
          }
          onClose={() => setIsMakingActivity(false)}
        />
      )}

      {/* Integration Manager */}
      <IntegrationManager
        userAccessToken={userAccessToken}
        userId={userId}
        showIntegrationSetup={showIntegrationSetup}
        onHide={handleSetUpIntegrationHide}
        allowedServices={allowedIntegrationServices}
      />
    </>
  );
};
